import { useMemo, useState } from 'react';

import { Badge } from '../../components/ui/Badge.js';
import { formatDate } from '../../utils/date.js';
import { useRowLink } from '../../utils/useRowLink.js';
import { ticketRefLabel } from '../tickets/reference.js';
import { useMemberOptions, useTimeEntries } from '../time-entries/api.js';
import {
  BILLING_STATUS_FILTERS,
  billingStatusOf,
  matchesBillingStatusFilter,
} from '../time-entries/billing-status.js';

interface AgreementTimeEntriesSectionProps {
  agreementId: string;
}

export function AgreementTimeEntriesSection({
  agreementId,
}: AgreementTimeEntriesSectionProps): JSX.Element {
  const entriesQuery = useTimeEntries({
    page: 1,
    pageSize: 200,
    agreementId,
    sortBy: 'workDate',
    sortDir: 'desc',
  });
  const membersQuery = useMemberOptions();
  const rowLink = useRowLink();

  const entries = entriesQuery.data?.data ?? [];
  const memberNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of membersQuery.data ?? []) {
      map.set(member.id, member.displayName);
    }
    return map;
  }, [membersQuery.data]);

  // Filters: a text search across every displayed field + a billing-status dropdown.
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return entries.filter((entry) => {
      if (!matchesBillingStatusFilter(entry, statusFilter)) return false;
      if (!term) return true;
      const status = billingStatusOf(entry);
      const haystack = [
        formatDate(entry.workDate),
        `${entry.hours}`,
        memberNames.get(entry.memberId) ?? entry.memberName ?? '',
        ticketRefLabel(entry.ticketReference, entry.ticketNumber),
        entry.notes ?? '',
        status.label,
        status.detail ?? '',
        entry.invoiceNumber ?? '',
        entry.externalInvoiceRef ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [entries, search, statusFilter, memberNames]);

  return (
    <div className="rounded-lg border border-slate-200">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-3 py-2">
        <span className="text-sm font-medium text-slate-700">Time entries</span>
        {entries.length > 0 ? (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by date, member, ticket, notes, status…"
              className="min-w-0 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              {BILLING_STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {entriesQuery.isLoading ? (
        <p className="px-3 py-2 text-sm text-slate-500">Loading time entries…</p>
      ) : entries.length === 0 ? (
        <p className="px-3 py-2 text-sm text-slate-500">No time entries logged yet.</p>
      ) : filtered.length === 0 ? (
        <p className="px-3 py-2 text-sm text-slate-500">No time entries match the filter.</p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium">Member</th>
              <th className="px-3 py-2 font-medium">Ticket</th>
              <th className="px-3 py-2 font-medium">Notes</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 text-right font-medium">Hours</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((entry) => {
              const status = billingStatusOf(entry);
              return (
                <tr
                  key={entry.id}
                  className="cursor-pointer hover:bg-slate-50"
                  {...rowLink(`/time-entries/${entry.id}`)}
                >
                  <td className="px-3 py-2 text-slate-600">{formatDate(entry.workDate)}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {memberNames.get(entry.memberId) ?? '—'}
                  </td>
                  <td className="px-3 py-2 tabular-nums">
                    {entry.ticketId ? (
                      <a
                        href={`/tickets/${entry.ticketId}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
                      >
                        {ticketRefLabel(entry.ticketReference, entry.ticketNumber)}
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-500">{entry.notes ?? '—'}</td>
                  <td className="px-3 py-2">
                    <Badge tone={status.tone}>
                      {status.detail ? `${status.label} · ${status.detail}` : status.label}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-800">
                    {entry.hours}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
